import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import AnalysisModal from "@/components/AnalysisModal";
import { Filter, Sparkles, LogIn, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

const SCREENS = [
  { id: "all", label: "Semua", fn: () => true },
  { id: "uptrend", label: "Momentum Naik", fn: (r) => r.trend === "UPTREND" && r.rsi != null && r.rsi >= 50 && r.rsi <= 72 },
  { id: "oversold", label: "Oversold (RSI<35)", fn: (r) => r.rsi != null && r.rsi < 35 },
  { id: "overbought", label: "Overbought (RSI>70)", fn: (r) => r.rsi != null && r.rsi > 70 },
  { id: "volume", label: "Volume Spike ≥2x", fn: (r) => r.vol_ratio != null && r.vol_ratio >= 2 },
  { id: "dividend", label: "Dividen Tinggi ≥5%", fn: (r) => r.div_yield != null && r.div_yield >= 5 },
  { id: "value", label: "Valuasi Murah", fn: (r) => r.pbv != null && r.pbv < 1.5 && r.per != null && r.per > 0 && r.per < 12 },
];

const rsiColor = (v) => (v == null ? "#64748B" : v > 70 ? "#F43F5E" : v < 35 ? "#10B981" : "#EAB308");
const trendPill = (t) => (t === "UPTREND" ? "bg-[var(--bull)]/15 text-bull" : t === "DOWNTREND" ? "bg-[var(--bear)]/15 text-bear" : "bg-[var(--surface-hover)] text-[var(--text-secondary)]");
const TrendIcon = ({ t }) => (t === "UPTREND" ? <TrendingUp size={12} /> : t === "DOWNTREND" ? <TrendingDown size={12} /> : <Minus size={12} />);
const num = (v, d = 2) => (v == null ? "-" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: d }));

const COLS = [
  { key: "symbol", label: "Emiten", align: "left" },
  { key: "price", label: "Harga", align: "right" },
  { key: "change_pct", label: "%", align: "right" },
  { key: "rsi", label: "RSI", align: "right" },
  { key: "trend", label: "Tren", align: "center" },
  { key: "vol_ratio", label: "Vol x", align: "right" },
  { key: "per", label: "PER", align: "right" },
  { key: "pbv", label: "PBV", align: "right" },
  { key: "div_yield", label: "Div%", align: "right" },
];

export default function ScreenerSection() {
  const { user, login } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [screen, setScreen] = useState("all");
  const [sort, setSort] = useState({ key: "change_pct", dir: "desc" });
  const [ticker, setTicker] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    if (!user) { setNeedLogin(true); return; }
    setLoading(true); setNeedLogin(false);
    try {
      const res = await api.get("/screener");
      setRows(res.data.rows || []);
    } catch (e) {
      if (e?.response?.status === 401) setNeedLogin(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const active = SCREENS.find((s) => s.id === screen) || SCREENS[0];
  let list = rows.filter(active.fn);
  list = [...list].sort((a, b) => {
    const av = a[sort.key], bv = b[sort.key];
    if (sort.key === "symbol" || sort.key === "trend") {
      const r = String(av || "").localeCompare(String(bv || ""));
      return sort.dir === "asc" ? r : -r;
    }
    const x = av == null ? -Infinity : av, y = bv == null ? -Infinity : bv;
    return sort.dir === "asc" ? x - y : y - x;
  });

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  const SortIcon = ({ k }) => (sort.key !== k ? <ArrowUpDown size={11} className="opacity-40" /> : sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />);
  const visible = showAll ? list : list.slice(0, 3);

  return (
    <section id="screener" className="scroll-mt-20">
      <SectionTitle eyebrow="Screener Kustom · 90+ Saham Likuid" title="Saring Saham Sesuai Kriteria" desc="Filter berdasarkan tren, RSI, lonjakan volume, dividen, dan valuasi (PER/PBV). Klik baris untuk Analisis AI." icon={Filter} />

      {needLogin ? (
        <div className="card p-12 text-center" data-testid="screener-login-prompt">
          <Sparkles size={26} className="text-accent mx-auto mb-2" />
          <p className="text-sm text-[var(--text-secondary)] mb-4">Masuk untuk membuka Screener Kustom 90+ saham likuid IDX.</p>
          <button onClick={login} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]"><LogIn size={16} /> Masuk dengan Google</button>
        </div>
      ) : (
        <div className="card p-5">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {SCREENS.map((s) => (
              <button key={s.id} onClick={() => { setScreen(s.id); setShowAll(false); }} data-testid={`screen-${s.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${screen === s.id ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-accent"}`}>{s.label}</button>
            ))}
            <button onClick={load} data-testid="screener-refresh" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-accent">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {loading ? "Memuat" : "Refresh"}
            </button>
          </div>

          {loading && rows.length === 0 ? (
            <div className="py-16 text-center"><div className="w-9 h-9 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-[var(--text-secondary)]">Memuat data pasar...</p></div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <div className="max-h-[520px] overflow-y-auto rounded-lg border border-[var(--border-subtle)]">
              <table className="w-full text-sm min-w-[720px]" data-testid="screener-table">
                <thead className="sticky top-0 z-10 bg-[var(--surface-card)]">
                  <tr className="text-[11px] text-[var(--text-muted)] uppercase tracking-wider border-b border-[var(--border)]">
                    {COLS.map((c) => (
                      <th key={c.key} onClick={() => toggleSort(c.key)} className={`py-2.5 px-2 cursor-pointer select-none hover:text-accent ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>
                        <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>{c.label} <SortIcon k={c.key} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.symbol} onClick={() => setTicker(r.symbol)} data-testid={`screener-row-${r.symbol}`} className="border-b border-[var(--border-subtle)] hover:bg-[var(--surface-hover)] cursor-pointer transition-colors">
                      <td className="py-2.5 px-2">
                        <p className="font-heading font-bold text-accent">{r.symbol}</p>
                        <p className="text-[11px] text-[var(--text-muted)] truncate max-w-[180px]">{r.name}</p>
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono-data">{num(r.price, 0)}</td>
                      <td className={`py-2.5 px-2 text-right font-mono-data font-semibold ${r.change_pct >= 0 ? "text-bull" : "text-bear"}`}>{r.change_pct >= 0 ? "+" : ""}{num(r.change_pct)}%</td>
                      <td className="py-2.5 px-2 text-right font-mono-data font-semibold" style={{ color: rsiColor(r.rsi) }}>{r.rsi == null ? "-" : num(r.rsi, 1)}</td>
                      <td className="py-2.5 px-2 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${trendPill(r.trend)}`}><TrendIcon t={r.trend} /> {r.trend?.slice(0, 4)}</span></td>
                      <td className={`py-2.5 px-2 text-right font-mono-data ${r.vol_ratio >= 2 ? "text-amber font-bold" : ""}`}>{r.vol_ratio == null ? "-" : num(r.vol_ratio) + "x"}</td>
                      <td className="py-2.5 px-2 text-right font-mono-data">{r.per == null ? "-" : num(r.per, 1)}</td>
                      <td className="py-2.5 px-2 text-right font-mono-data">{r.pbv == null ? "-" : num(r.pbv, 2)}</td>
                      <td className={`py-2.5 px-2 text-right font-mono-data ${r.div_yield >= 5 ? "text-bull font-bold" : ""}`}>{r.div_yield == null ? "-" : num(r.div_yield, 1) + "%"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {list.length === 0 && <p className="text-center text-sm text-[var(--text-muted)] py-10">Tidak ada saham yang cocok dengan filter ini.</p>}
              {list.length > 3 && (
                <button onClick={() => setShowAll((v) => !v)} data-testid="screener-more" className="mt-3 w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
                  {showAll ? "Tampilkan lebih sedikit" : `Selengkapnya (${list.length - 3} saham lainnya)`}
                </button>
              )}
            </div>
          )}
          <p className="text-[10px] text-[var(--text-muted)] mt-3">Menampilkan {visible.length} dari {list.length} saham (filter aktif) · data teknikal 3 bulan (harian) + valuasi. Edukasi, DYOR.</p>
        </div>
      )}
      {ticker && <AnalysisModal ticker={ticker} onClose={() => setTicker(null)} />}
    </section>
  );
}
